import { FC, useMemo, useEffect, useContext } from 'react';
import { graphql, createRefetchContainer, RelayRefetchProp } from 'react-relay/legacy';
import { useSelector } from 'react-redux';
import Helmet from 'react-helmet';

import { RecentlyViewedItemsProvider } from 'dibs-recent-history/exports/RecentlyViewedItemsProvider';
import { ServerVarsContext } from '../global/ServerVarsContext/ServerVarsContext';
import { PdpRespContainer } from './PdpRespContainer/PdpRespContainer';
import { PdpSharedLayout } from './PdpSharedLayout';
import { PdpSharedAuctionContextProvider } from './PdpSharedAuctionContext/PdpSharedAuctionContext';
import { getPersonalizationTrackingData } from './tracking/personalizationTracking';

import { PdpRespLayout_viewer$data } from './__generated__/PdpRespLayout_viewer.graphql';

import './styles/PdpRespLayout.scss';

export const PdpRespLayoutComponent: FC<{
    viewer: PdpRespLayout_viewer$data;
    relay: RelayRefetchProp;
}> = ({ viewer, relay }) => {
    const { isPdpMetaDescRemovalVariant, isTablet } = useContext(ServerVarsContext);
    const { isPdpRespPreviewPage } = useSelector<$TSFixMe, { isPdpRespPreviewPage: boolean }>(
        state => ({
            isPdpRespPreviewPage: state?.pdpPreviewPage?.isPdpRespPreviewPage,
        })
    );

    useEffect(() => {
        if (isPdpRespPreviewPage) {
            document.body.classList.add('disable-click-on-preview');
        }
    }, [isPdpRespPreviewPage]);

    const { similarLandingItems, personalizedItemsList } = viewer || {};

    const user = viewer.user || null;

    const item = viewer.itemRead?.[0] || null;
    const displayInfo = item?.displayInfo || null;

    const additionalTrackingData = useMemo(() => {
        return getPersonalizationTrackingData({ personalizedItems: personalizedItemsList });
    }, [personalizedItemsList]);

    if (!item) {
        return null;
    }

    return (
        <RecentlyViewedItemsProvider
            excludedItemId={item.serviceId}
            maxItems={isTablet ? 10 : 12}
            itemsPerPage={isTablet ? 5 : 6}
        >
            <PdpSharedAuctionContextProvider>
                <PdpSharedLayout
                    user={user}
                    viewer={viewer}
                    refreshPageOnTradeLogin={false}
                    additionalTrackingData={additionalTrackingData}
                    doRefetch={relay.refetch}
                    environment={relay.environment}
                >
                    {({ pdpHead, currency, onSkuChange, selectedSkuId }: $TSFixMe) => (
                        <>
                            <Helmet
                                {...pdpHead.getPdpData({
                                    hideDescription: isPdpMetaDescRemovalVariant,
                                })}
                            />
                            <PdpRespContainer
                                viewer={viewer}
                                user={user}
                                item={item}
                                itemSearch={null}
                                currency={currency}
                                onSkuChange={onSkuChange}
                                displayInfo={displayInfo}
                                selectedSkuId={selectedSkuId}
                                similarLandingItems={similarLandingItems}
                            />
                        </>
                    )}
                </PdpSharedLayout>
            </PdpSharedAuctionContextProvider>
        </RecentlyViewedItemsProvider>
    );
};

//TODO: refactor to useRefechableFragment when PdpSharedLayout is converted to typescript
export const PdpRespLayout = createRefetchContainer(
    PdpRespLayoutComponent,
    {
        viewer: graphql`
            fragment PdpRespLayout_viewer on Viewer {
                ...PdpRespContainer_viewer
                ...PdpSharedLayout_viewer
                user(userId: $userId) @include(if: $fetchUser) {
                    ...PdpRespContainer_user
                    ...PdpSharedLayout_user
                }
                itemRead(itemIds: $itemIds) {
                    ... on Item {
                        serviceId
                        ...PdpRespContainer_item
                        displayInfo(page: pdp, buyerId: $userId) @include(if: $isClient) {
                            ...PdpRespContainer_displayInfo
                        }
                    }
                }
                similarLandingItems: moreLikeThisItem(
                    first: 12
                    itemId: $itemId
                    isTrade: $isTrade
                    onlyAuctionItems: true
                    auctionModule: true
                ) @include(if: $fetchPdp) {
                    metadata {
                        isUsingRerankedResults
                    }
                    ...PdpRespContainer_similarLandingItems
                }
                personalizedItemsList: personalizedItems(
                    userId: $userId
                    itemId: $itemId
                    pageSize: 10
                ) @include(if: $isClient) {
                    associationType
                    hasPersonalizedItem
                }
            }
        `,
    },
    graphql`
        query PdpRespLayoutRefetchQuery(
            $isClient: Boolean!
            $itemId: String!
            $itemIds: [String]!
            $userId: String = ""
            $userIds: [String]
            $fetchUser: Boolean!
            $followSearchPages: [String]
            $fetchFollowSearch: Boolean!
            $isTrade: Boolean!
            $isMultiSku: Boolean!
            $fetchPdp: Boolean!
            $forcedCurrency: Currency
            $userZipCode: String = ""
            $userCountryCode: String = ""
            $fetchRegionalInfo: Boolean = false
            $priceBookName: String = ""
            $localRecentHistoryItemIds: [String] = []
            $fetchUserRecentHistoryItem: Boolean = false
            $isInteractivePdpChat: Boolean = false
            $timezoneOffset: Int
            $fetchAuction: Boolean = false
            # TOF-690: Add PDP Resp About tabs
            $isPdpRespAboutTabsFF: Boolean = false
            $itemQuantity: Int
            # TOF-634: PDP Chat Prompts
            $showBuyerChatSuggestions: Boolean = false
            # TOF-827: PDP resp tabs redesign A/B test
            $isRespTabsRedesignVariant: Boolean = false
        ) {
            viewer {
                ...PdpRespLayout_viewer
            }
        }
    `
);
